import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import AnalysisDashboard from '@/components/AnalysisDashboard';
import { useDelayedSkeleton } from '@/hooks/useDelayedSkeleton';
import { authManager } from '@/services/authManager';

interface Player {
  id: string;
  name: string;
  nickname?: string;
}

const AnalysisPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { showSkeleton, showContent } = useDelayedSkeleton({ loading });

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await authManager.getAccessToken();
        const response = await fetch('/api/v1/players', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch players');
        }

        const data = await response.json();
        setPlayers(data.players || []);
        
        // Auto-select first player if available
        if (data.players && data.players.length > 0) {
          setSelectedPlayerId(data.players[0].id);
        }
      } catch (err) {
        console.error('Error fetching players:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  // Show skeleton if needed
  if (showSkeleton) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 1.5, sm: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon />
          Analysis Dashboard
        </Typography>
        <Box sx={{ mb: 3, maxWidth: 300 }}>
          <Alert severity="info">Loading players...</Alert>
        </Box>
      </Container>
    );
  }

  // Return null while loading - loading bar shows progress
  if (!showContent) return null;

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 1.5, sm: 3 } }}>
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (players.length === 0) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 1.5, sm: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon />
          Analysis Dashboard
        </Typography>
        <Alert severity="info">
          No players found. Please add players and assign them to leagues or tournaments to see analysis data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon />
        Analysis Dashboard
      </Typography>

      <Box sx={{ mb: 3, maxWidth: 300 }}>
        <FormControl fullWidth>
          <InputLabel id="player-select-label">Select Player</InputLabel>
          <Select
            labelId="player-select-label"
            id="player-select"
            value={selectedPlayerId}
            label="Select Player"
            onChange={(e) => setSelectedPlayerId(e.target.value)}
          >
            {players.map((player) => (
              <MenuItem key={player.id} value={player.id}>
                {player.nickname || player.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedPlayerId && (
        <AnalysisDashboard playerId={selectedPlayerId} />
      )}
    </Container>
  );
};

export default AnalysisPage;
